import { HugeiconsIcon } from '@hugeicons/react';
import {
  Add01Icon,
  Loading02Icon,
  AlertCircleIcon,
  RefreshIcon,
  Cancel01Icon,
  Tick02Icon,
  Tick01Icon,
  CancelCircleIcon,
  UserGroupIcon,
  UserAdd01Icon,
} from '@hugeicons/core-free-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// AgentAccessPanel — administra el acceso a UN agente (RF-7.2/7.3, CA-14/CA-15):
// grants por grupo y reglas individuales allow/deny. El deny individual SIEMPRE
// gana sobre cualquier grant (la precedencia la resuelve el backend; aquí solo
// se administra y se distingue visualmente).
//
// Endpoints (prefijo real /api lo añade el cliente axios):
//   GET    /insights/agents/{id}/access -> {groups, allow_users, deny_users}
//   GET    /insights/groups             -> [InsightsGroup] (para el multiselect)
//   POST   /insights/agents/{id}/access/groups {group_id}
//   DELETE /insights/agents/{id}/access/groups/{groupId}
//   PUT    /insights/agents/{id}/access/users/{userId} {effect:'allow'|'deny'}
//   DELETE /insights/agents/{id}/access/users/{userId}
//   GET    /users/search?q=             -> [{id,name,email,initials,...}]

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_CHARS = 2;

// Multiselect de grupos (Popover + Command) alimentado por GET /insights/groups.
// Marca los grupos ya con grant; al togglear invoca onToggle(groupId, yaConcedido).
function GroupsMultiselect({ allGroups, grantedIds, onToggle, disabled }) {
  const [open, setOpen] = useState(false);
  const grantedSet = useMemo(() => new Set(grantedIds), [grantedIds]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start text-muted-foreground sm:w-80"
          disabled={disabled}
          aria-label="Agregar grupos con acceso">
          <HugeiconsIcon icon={Add01Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
          Agregar grupos…
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(24rem,calc(100vw-2rem))] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar grupo…" />
          <CommandList>
            <CommandEmpty>No hay grupos que coincidan</CommandEmpty>
            {allGroups.map((g) => {
              const concedido = grantedSet.has(g.id);
              return (
                <CommandItem
                  key={g.id}
                  value={`${g.name} ${g.id}`}
                  onSelect={() => onToggle(g.id, concedido)}
                  className="group items-center">
                  <HugeiconsIcon
                    icon={Tick01Icon}
                    size={16}
                    strokeWidth={1.5}
                    aria-hidden="true"
                    className={cn(
                      'mr-2 text-primary group-data-[selected=true]:text-accent-foreground',
                      concedido ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate text-foreground group-data-[selected=true]:text-accent-foreground">
                    {g.name}
                  </span>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Buscador de usuarios server-side (GET /users/search). Al elegir dispara
// onPick(user) para añadir una regla individual.
function UserSearchPicker({ onPick, placeholder = 'Buscar usuario por nombre o correo…' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const q = query.trim();
    if (timer.current) clearTimeout(timer.current);
    if (q.length < SEARCH_MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/users/search', { params: { q } });
        setResults(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(apiError(e) || 'No se pudo buscar usuarios');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-start text-muted-foreground sm:w-80">
          <HugeiconsIcon icon={UserAdd01Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
          Agregar persona…
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(28rem,calc(100vw-2rem))] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={placeholder} value={query} onValueChange={setQuery} />
          <CommandList>
            {loading ? (
              <div
                className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground"
                role="status"
                aria-live="polite">
                <HugeiconsIcon
                  icon={Loading02Icon}
                  size={16}
                  strokeWidth={1.5}
                  className="animate-spin"
                  aria-hidden="true"
                />
                Buscando…
              </div>
            ) : (
              <>
                <CommandEmpty>
                  {query.trim().length < SEARCH_MIN_CHARS ? 'Escribe al menos 2 caracteres' : 'Sin resultados'}
                </CommandEmpty>
                {results.map((u) => (
                  <CommandItem
                    key={u.id}
                    value={u.id}
                    onSelect={() => {
                      onPick(u);
                      setOpen(false);
                      setQuery('');
                    }}
                    className="items-start">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-foreground">{u.name || u.id}</span>
                      {u.email ? (
                        <span className="mt-0.5 block truncate text-xs text-muted-foreground">{u.email}</span>
                      ) : null}
                    </span>
                  </CommandItem>
                ))}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Fila de regla individual de usuario con control allow/deny y botón de quitar.
// allow => token primario/éxito + icono Tick02Icon + etiqueta "Con acceso".
// deny  => token destructive + icono CancelCircleIcon + etiqueta "Sin acceso".
// El texto acompaña al color (no solo color) para contraste AA (CA-15).
function UserAccessRow({ item, onSetEffect, onRemove }) {
  const esDeny = item.effect === 'deny';
  return (
    <li
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2',
        esDeny ? 'border-destructive/40 bg-destructive/5' : 'border-border/80 bg-card',
      )}>
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium',
          esDeny ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary',
        )}>
        {item.initials || (item.name ? item.name.slice(0, 2).toUpperCase() : '?')}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{item.name || item.user_id}</p>
        {item.email ? <p className="truncate text-xs text-muted-foreground">{item.email}</p> : null}
      </div>

      <Badge
        variant="outline"
        className={cn(
          'gap-1',
          esDeny
            ? 'border-destructive/40 bg-destructive/10 text-destructive'
            : 'border-primary/40 bg-primary/10 text-primary',
        )}>
        <HugeiconsIcon icon={esDeny ? CancelCircleIcon : Tick02Icon} size={13} strokeWidth={1.5} aria-hidden="true" />
        {esDeny ? 'Sin acceso' : 'Con acceso'}
      </Badge>

      {/* Control allow/deny: dos botones, el activo resaltado con su token. */}
      <div
        className="inline-flex overflow-hidden rounded-md border border-border"
        role="group"
        aria-label={`Acceso de ${item.name || 'usuario'}`}>
        <button
          type="button"
          onClick={() => onSetEffect(item, 'allow')}
          aria-pressed={!esDeny}
          className={cn(
            'px-2.5 py-1 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset',
            !esDeny ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted',
          )}>
          Permitir
        </button>
        <button
          type="button"
          onClick={() => onSetEffect(item, 'deny')}
          aria-pressed={esDeny}
          className={cn(
            'border-l border-border px-2.5 py-1 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-inset',
            esDeny ? 'bg-destructive/10 text-destructive' : 'text-muted-foreground hover:bg-muted',
          )}>
          Denegar
        </button>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onRemove(item)}
        className="h-8 text-muted-foreground hover:text-foreground"
        aria-label={`Quitar la regla de ${item.name || 'usuario'}`}>
        <HugeiconsIcon icon={Cancel01Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
        Quitar
      </Button>
    </li>
  );
}

export default function AgentAccessPanel({ agentId }) {
  const queryClient = useQueryClient();
  const [access, setAccess] = useState({ groups: [], allow_users: [], deny_users: [] });
  const [allGroups, setAllGroups] = useState([]);
  const [state, setState] = useState({ loading: true, error: '' });

  // Tras cada cambio de acceso, invalida la lista de agentes disponibles que ve
  // el usuario final (/insights) para que aparezca/desaparezca sin recargar.
  // loadAccess() sigue refrescando esta vista admin; esto solo añade la frescura
  // de la lista del usuario. La seguridad real vive en el 403 por request.
  const invalidarAgentesDisponibles = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['insights', 'agents', 'available'] });
  }, [queryClient]);

  const loadAccess = useCallback(async () => {
    setState({ loading: true, error: '' });
    try {
      const [accessRes, groupsRes] = await Promise.all([
        api.get(`/insights/agents/${agentId}/access`),
        api.get('/insights/groups'),
      ]);
      const a = accessRes.data || {};
      setAccess({
        groups: Array.isArray(a.groups) ? a.groups : [],
        allow_users: Array.isArray(a.allow_users) ? a.allow_users : [],
        deny_users: Array.isArray(a.deny_users) ? a.deny_users : [],
      });
      setAllGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      setState({ loading: false, error: '' });
    } catch (e) {
      setState({ loading: false, error: apiError(e) });
    }
  }, [agentId]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const grantedGroupIds = useMemo(() => access.groups.map((g) => g.group_id), [access.groups]);

  const toggleGroup = async (groupId, yaConcedido) => {
    try {
      if (yaConcedido) {
        await api.delete(`/insights/agents/${agentId}/access/groups/${groupId}`);
        toast.success('Grupo quitado del acceso');
      } else {
        await api.post(`/insights/agents/${agentId}/access/groups`, { group_id: groupId });
        toast.success('Grupo con acceso');
      }
      await loadAccess();
      invalidarAgentesDisponibles();
    } catch (e) {
      toast.error(apiError(e) || 'No se pudo actualizar el acceso del grupo');
    }
  };

  const quitarGrupo = async (groupId) => {
    try {
      await api.delete(`/insights/agents/${agentId}/access/groups/${groupId}`);
      toast.success('Grupo quitado del acceso');
      await loadAccess();
      invalidarAgentesDisponibles();
    } catch (e) {
      toast.error(apiError(e) || 'No se pudo quitar el grupo');
    }
  };

  const setUserEffect = async (user, effect) => {
    const userId = user.user_id || user.id;
    try {
      await api.put(`/insights/agents/${agentId}/access/users/${userId}`, { effect });
      toast.success(effect === 'deny' ? 'Acceso denegado a la persona' : 'Acceso permitido a la persona');
      await loadAccess();
      invalidarAgentesDisponibles();
    } catch (e) {
      toast.error(apiError(e) || 'No se pudo actualizar el acceso de la persona');
    }
  };

  const removeUser = async (user) => {
    const userId = user.user_id || user.id;
    try {
      await api.delete(`/insights/agents/${agentId}/access/users/${userId}`);
      toast.success('Regla de la persona eliminada');
      await loadAccess();
      invalidarAgentesDisponibles();
    } catch (e) {
      toast.error(apiError(e) || 'No se pudo quitar la regla');
    }
  };

  // Al elegir una persona en el buscador se crea su regla con allow por defecto.
  const agregarPersona = (u) => setUserEffect(u, 'allow');

  const usuarios = useMemo(() => {
    const allow = access.allow_users.map((u) => ({ ...u, effect: 'allow' }));
    const deny = access.deny_users.map((u) => ({ ...u, effect: 'deny' }));
    return [...allow, ...deny].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [access.allow_users, access.deny_users]);

  if (state.loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground" role="status" aria-live="polite">
        <HugeiconsIcon icon={Loading02Icon} size={18} strokeWidth={1.5} className="animate-spin" aria-hidden="true" />
        Cargando acceso…
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive" role="alert">
        <HugeiconsIcon icon={AlertCircleIcon} size={18} strokeWidth={1.5} aria-hidden="true" />
        <span className="flex-1">{state.error}</span>
        <button
          type="button"
          onClick={loadAccess}
          className="inline-flex items-center gap-1 font-medium hover:underline">
          <HugeiconsIcon icon={RefreshIcon} size={14} strokeWidth={1.5} aria-hidden="true" />
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Grupos con grant */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <HugeiconsIcon icon={UserGroupIcon} size={16} strokeWidth={1.5} aria-hidden="true" className="text-primary" />
          <Label className="text-sm font-medium text-foreground">Grupos con acceso</Label>
        </div>
        <GroupsMultiselect
          allGroups={allGroups}
          grantedIds={grantedGroupIds}
          onToggle={toggleGroup}
          disabled={allGroups.length === 0}
        />
        {allGroups.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Aún no hay grupos. Crea grupos en la pestaña «Grupos» para concederles acceso.
          </p>
        ) : null}
        {access.groups.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {access.groups.map((g) => (
              <Badge key={g.group_id} variant="secondary" className="gap-1">
                {g.group_name || g.group_id}
                <button
                  type="button"
                  onClick={() => quitarGrupo(g.group_id)}
                  aria-label={`Quitar acceso del grupo ${g.group_name || g.group_id}`}
                  className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-secondary-foreground/70 transition-colors hover:text-secondary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <HugeiconsIcon icon={Cancel01Icon} size={12} strokeWidth={2} aria-hidden="true" />
                </button>
              </Badge>
            ))}
          </div>
        ) : (
          <p
            className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground"
            role="status">
            Ningún grupo tiene acceso a este agente todavía.
          </p>
        )}
      </section>

      {/* Reglas individuales (allow/deny) */}
      <section className="flex flex-col gap-3">
        <Label className="text-sm font-medium text-foreground">Personas (acceso individual)</Label>
        <p className="text-xs text-muted-foreground">
          Permite o deniega el acceso a personas concretas. Denegar a una persona le quita el acceso aunque pertenezca a
          un grupo con acceso.
        </p>
        <UserSearchPicker onPick={agregarPersona} />
        {usuarios.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {usuarios.map((u) => (
              <UserAccessRow key={u.user_id} item={u} onSetEffect={setUserEffect} onRemove={removeUser} />
            ))}
          </ul>
        ) : (
          <p
            className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground"
            role="status">
            No hay reglas individuales para este agente.
          </p>
        )}
      </section>
    </div>
  );
}
